// ============================================================================
// ORGANISATION MASTER SERVICE
// ============================================================================
// Entities, business units and financial years of one tenant, through the
// tenant-scoped organisation endpoints. Every write lands in the Hub's
// organisation change log; the response carries the row's new version.

import { get, post, put, del } from '../client/axiosClient';
import { organisationEndpoints } from '../endpoints/organisation.endpoints';
import type { ID } from '../types';
import type { TenantHubApiResponse } from '../types/auth.types';
import type { TenantHubPagedResult } from '../types/tenants.types';
import type { OrganisationKind, OrganisationSearchParams } from '../types/organisation.types';

class OrganisationService {
  /** Paged, searchable list of one kind for one tenant. */
  async list<T>(kind: OrganisationKind, tenantId: ID, params?: OrganisationSearchParams):
    Promise<TenantHubApiResponse<TenantHubPagedResult<T>>> {
    const response = await get<TenantHubApiResponse<TenantHubPagedResult<T>>>(
      organisationEndpoints.list(tenantId, kind),
      { params: params ? { ...params, statusCode: params.statusCode || undefined } : undefined },
    );
    return response.data;
  }

  async getById<T>(kind: OrganisationKind, tenantId: ID, id: ID): Promise<TenantHubApiResponse<T>> {
    const response = await get<TenantHubApiResponse<T>>(organisationEndpoints.byId(tenantId, kind, id));
    return response.data;
  }

  async create<T, R = unknown>(kind: OrganisationKind, tenantId: ID, data: R): Promise<TenantHubApiResponse<T>> {
    const response = await post<TenantHubApiResponse<T>>(organisationEndpoints.list(tenantId, kind), data);
    return response.data;
  }

  async update<T, R = unknown>(kind: OrganisationKind, tenantId: ID, id: ID, data: R): Promise<TenantHubApiResponse<T>> {
    const response = await put<TenantHubApiResponse<T>>(organisationEndpoints.byId(tenantId, kind, id), data);
    return response.data;
  }

  /** Soft delete; projections receive it as a 'deleted' change carrying ORG_INACTIVE. */
  async remove(kind: OrganisationKind, tenantId: ID, id: ID): Promise<TenantHubApiResponse<boolean>> {
    const response = await del<TenantHubApiResponse<boolean>>(organisationEndpoints.byId(tenantId, kind, id));
    return response.data;
  }

  async setCurrentFinancialYear(tenantId: ID, id: ID): Promise<TenantHubApiResponse<boolean>> {
    const response = await post<TenantHubApiResponse<boolean>>(organisationEndpoints.setCurrent(tenantId, id));
    return response.data;
  }

  async closeFinancialYear(tenantId: ID, id: ID): Promise<TenantHubApiResponse<boolean>> {
    const response = await post<TenantHubApiResponse<boolean>>(organisationEndpoints.close(tenantId, id));
    return response.data;
  }
}

export const organisationService = new OrganisationService();
export default organisationService;
